import React, { useEffect, useMemo, useState } from "react";
import { Banknote, TriangleAlert } from "lucide-react";
import Button from "../common/Button";
import Notice from "../common/Notice";
import PairRow from "./PairRow";
import RejectionNotice from "./RejectionNotice";
import { STUB_STRATEGIES, DEMO_DATA_LABEL, LIVE_DATA_LABEL } from "./stubData";
import { fmtUsd, fmtUsdc, clamp, shortAddr } from "./format";
import {
  wireKey,
  readSizingFloors,
  floorFor,
  isBelowFloor,
  belowFloorSummary,
  belowFloorDetail,
  investedTotal,
  overAllocatedBy,
} from "./allocationModel";

/**
 * WalletSection — one connected wallet with its strategy rows + cash row.
 *
 * REAL: address (member's own wallet via viem), delegate status + collateral
 * (/status), per-pair automated state (/status.riskConfig.pairAllowlist) and
 * open values (/status.openPositions), Revoke (on-chain removeTradingDelegate),
 * and — #9 — allocation/leverage PERSISTENCE: rows seed from
 * /status.riskConfig.allocations and Save posts them back (allocation-only
 * POST /config via onSaveAllocations; needs the human-applied
 * patches/signalsApi-allocations.patch, else Save stays disabled).
 * EXECUTION is still gated: saved allocations do not size trades until the
 * engine's audit-gated ALLOCATION_SIZING_ENABLED flag is armed — hence the
 * "doesn't trade yet" badge stays.
 *
 * STUB:
 *  - Wallet nickname persists in localStorage only ("saved on this device").
 *    TODO(backend, supervised): move nicknames to the engine's per-user
 *    Firestore record so they follow the member across devices (decided
 *    2026-07-14; localStorage stays until that backend exists).
 *  - Supernova slot pool: LIVE off GET /status.staking (ARC-53).
 *    TODO(backend, supervised): signal-time enforcement is still ARC-43.
 *
 * SUP-6 adds the slot cross-links: a locked row offers "Use open slot" or a link
 * to the Supernova tab, and an automated row whose stake left shows as paused.
 * They arrive as optional props and render only when the Supernova tab is
 * flagged on — display states, never an enforcement claim.
 *
 * ARC-32 makes the unlock count LIVE and restyles this card as the mockup's v3
 * glass console (`.sa-console` — see automation.css).
 *
 * ARC-53 replaces the count with the engine's slot pool and DELETES the
 * placeholder fallback. `slotGate` carries the whole decision (see slotGate.js):
 *
 *   engaged: false        no slot gate applies — no row is locked by stake
 *   unlockedCount: N      the pool is readable and holds N slots
 *   unlockedCount: null   we do not have an answer ⇒ EVERY row locks
 *
 * The last line is the point. This card used to fall back to a stub count when
 * the read failed, which unlocked rows on a number nobody had verified; with no
 * engine-side gate behind it (ARC-43 unbuilt) that is the one failure direction
 * that can cost a member money. Unknown now locks.
 *
 * ARC-45 corrects WHICH number gates a row: `slotsAvailable` (the room left),
 * not `slotsTotal` (the size of the pool). Gating on the total unlocked the
 * first N rows whoever actually held the slots, so a member with every slot
 * spent could still find an un-allocated row editable. It also adds the slot
 * counter, the lifecycle banner (gateBanner.js) and the per-wallet breakdown
 * (PerWalletStake.js) — all presentation of engine truth. Enforcement is
 * server-side in ARC-43 and none of this may be the only thing standing
 * between a member and an unauthorized trade.
 */

const nameKey = (account) => `arc-sa-wallet-name-${(account || "").toLowerCase()}`;

/* The line that stays true whatever the member does here. Allocation EXECUTION
   is PLAN, not built: the engine sizes every trade the same way it did before
   these numbers existed, and no copy on this tab may imply otherwise. */
export const UNARMED_STATUS_LINE =
  "Automated sizing goes live after the safety rollout — trades currently use fixed sizing.";
const SAVED_STATUS_LINE = `Allocations saved. ${UNARMED_STATUS_LINE}`;

const WalletSection = ({
  account,
  status,
  onRevoke,
  revoking,
  onSaveAllocations,
  savingAllocations,
  allocationsSupported,
  /* Supernova slot cross-links (SUP-6) — all optional. Absent (every build with
     SUPERNOVA_TAB_ENABLED off) ⇒ the rows render exactly as they did before. */
  slots = null, // deriveSlots() result from the tab
  onUseOpenSlot, // (pair) => void — takes the member to the allowlist control
  onStakeMore, // () => void — opens the Supernova tab
  /* ARC-53 — the gate, whole. Omitted ⇒ no slot gating at all, which is what
     every build with the Supernova feature off gets. */
  slotGate = null,
  /* ARC-34 — has the engine answered? Drives which of the two data labels this
     console carries. False is the honest default: unlabelled is not a state. */
  engineLive = false,
  /* GET /report positions, for per-pair PnL. /status carries size and
     collateral but no PnL, so without this the money cell says "PnL —". */
  reportPositions = null,
  /* SUPERBETA-8 — the TradingView connect wizard. `connectedPairKeys` is a Set
     of engine-normalized pair keys the engine has received a signal for; it is
     the engine's answer, not a local flag. Omitting `onConnectTradingView`
     renders no connect affordance anywhere and leaves this console unchanged. */
  connectedPairKeys = null, // Set<string> | null
  onConnectTradingView, // (pair) => void | undefined
  /* ARC-54 — the engine's 400 for the LAST allocation save, if it named keys it
     didn't recognise. Rendered on the save row, beside the control that caused
     it; the tab-level banner carries it as well and remains the backstop. */
  rejection = null, // { message, keys[] } | null
}) => {
  /* nickname — device-local only */
  const [name, setName] = useState("My trading wallet");
  useEffect(() => {
    if (!account) return;
    try {
      const saved = localStorage.getItem(nameKey(account));
      if (saved) setName(saved);
    } catch {
      /* localStorage unavailable — keep default */
    }
  }, [account]);
  const renameWallet = (next) => {
    setName(next);
    try {
      localStorage.setItem(nameKey(account), next);
    } catch {
      /* non-fatal */
    }
  };

  /* strategy rows: allowlisted pairs are "Automated"; the stub list supplies
     the pair catalog (and preview defaults before any backend state exists);
     one extra row renders the Locked state. The allowlist arrives normalized
     ("BTCUSD") — compare via wireKey. */
  const allowlist = useMemo(
    () =>
      Array.isArray(status?.riskConfig?.pairAllowlist)
        ? status.riskConfig.pairAllowlist
        : [],
    [status]
  );
  const allowSet = useMemo(() => new Set(allowlist.map(wireKey)), [allowlist]);
  const [rows, setRows] = useState(
    STUB_STRATEGIES.map((s) => ({ ...s }))
  );
  const [dirty, setDirty] = useState(false);
  /* Result of the LAST save attempt: { confirmed } once the engine's echo has
     been checked. Cleared the moment the member edits again — a stale "saved"
     next to changed numbers is the lie this issue exists to prevent. */
  const [saveResult, setSaveResult] = useState(null);
  /* A save the member has been warned about but not yet confirmed:
     { payload, pairs } while the "Save anyway" prompt is on screen. */
  const [pendingBelowFloor, setPendingBelowFloor] = useState(null);
  const updateRow = (pair, patch) => {
    setDirty(true);
    setSaveResult(null);
    setPendingBelowFloor(null);
    setRows((rs) => rs.map((r) => (r.pair === pair ? { ...r, ...patch } : r)));
  };

  /* Seed rows from the engine's SAVED allocations (#9) whenever a fresh
     /status lands and there are no unsaved local edits. Server state is the
     source of truth: a pair with no saved entry shows 0%, never a stub seed —
     stub defaults only render before any backend riskConfig exists. */
  useEffect(() => {
    if (dirty || !status?.riskConfig) return;
    const saved = status.riskConfig.allocations || {};
    setRows((rs) =>
      rs.map((r) => {
        const a = saved[wireKey(r.pair)];
        return a
          ? { ...r, allocationPct: a.pct, leverage: a.leverage ?? r.leverage }
          : { ...r, allocationPct: 0 };
      })
    );
  }, [status, dirty]);

  /**
   * Allocation editing under the sum<=100% invariant (SUPERBETA-6).
   *
   * The member's own number is kept exactly as typed, up to 100 per pair. An
   * over-allocation is SHOWN as one — in the sum bar, and by blocking the save
   * — rather than silently rewritten to fit, which is what this control used to
   * do. Quietly editing someone's input is the same failure class as quietly
   * discarding it: the screen stops matching what they asked for.
   *
   * The engine holds the same line server-side: sum > 100 rejects the whole
   * patch and never partially applies it, so a blocked save is exactly what
   * would have happened anyway, minus the round trip.
   */
  const updateAllocation = (pair, requested) => {
    updateRow(pair, { allocationPct: clamp(Number(requested) || 0, 0, 100) });
  };

  /* ── the gate (ARC-53, ARC-45) ───────────────────────────────────────────
     One staked Supernova unlocks one strategy, and every count here is the
     engine's — never a local guess. Three cases, and the third is the one
     ARC-53 exists for:

       not engaged           no stake gate at all; nothing locks on stake
       a readable pool       the member may automate `slotsAvailable` more
       engaged but unknown   loading, request failed, or the stake index is
                             unhealthy ⇒ EVERY row locks

     `gateKnown` is about the pool existing at all; `availableKnown` below is
     about the room left in it, and BOTH have to hold before a row unlocks or a
     digit is rendered. Neither unknown is ever shown as a zero — the caption
     and badge for an unread pool say we could not read it, and the
     digit-bearing copy only exists in the ready state. */
  const gateEngaged = Boolean(slotGate?.engaged);
  const gateKnown = typeof slotGate?.unlockedCount === "number" && slotGate.unlockedCount >= 0;

  /* ── Supernova slot states (SUP-6, display only) ─────────────────────────
     A locked row offers "Use open slot" when the stake has one going spare and
     a link to the Supernova tab when it doesn't. An automated row whose slot
     lost its stake shows as paused — and WHICH pairs those are is the engine's
     `unbackedPairs`, not a comparison made here. */
  const hasFreeSlot = Boolean(slotGate?.slotsAvailable > 0);
  const pausedKeys = new Set(
    (slotGate?.unbackedPairs || []).map((p) => wireKey(p.pair))
  );

  /* ── how many MORE strategies the member may automate (ARC-45) ────────────
     The engine's `slotsAvailable` — the slots not already spoken for — NOT
     `slotsTotal`. The distinction is the bug this replaces: gating on the total
     unlocks the first N ROWS, whoever holds the slots. A member with 2 slots,
     both spent on the pairs at rows 2 and 3, would find row 1 editable with
     nothing free to back it, because the arithmetic only ever counted rows.

     Unknown is not zero-then-unlock: an engine that sent a total but no
     available count has not told us how much room is left, so every
     un-allocated row locks and says the read failed rather than naming a
     number nobody has. Same fail-closed line as slotGate.js. */
  const availableKnown =
    typeof slotGate?.slotsAvailable === "number" && slotGate.slotsAvailable >= 0;
  const availableCount = availableKnown ? slotGate.slotsAvailable : 0;

  /* Position among the rows the member has NOT already automated. Locking is
     positional within that list — the member may turn on the next
     `availableCount` of them — so an automated pair must not consume a place in
     it, or allocating one pair would silently lock an unrelated row. */
  const unallocatedRank = new Map();
  rows
    .filter((r) => !allowSet.has(wireKey(r.pair)))
    .forEach((r, j) => unallocatedRank.set(wireKey(r.pair), j));

  /* Which rows read as locked. With no gate engaged, none of them are. An
     ALREADY AUTOMATED pair is never locked: the member has allocated it, so
     when their stake stops covering it the honest rendering is "paused", not
     "locked" — which pairs those are is the engine's `unbackedPairs`. */
  const rowLocked = (row) => {
    if (!gateEngaged) return false;
    if (!gateKnown || !availableKnown) return true; // an answer we don't have locks
    if (allowSet.has(wireKey(row.pair))) return false;
    return (unallocatedRank.get(wireKey(row.pair)) ?? 0) >= availableCount;
  };
  /* The row the empty-state CTA aims at: the first one the member can actually
     drive. Undefined ⇒ every row is stake-locked, and there is nothing to
     connect a signal to yet. */
  const firstUnlockedPair = rows.find((r) => !rowLocked(r))?.pair;

  const lockedCta = (pair) => {
    /* Usage unknown (no answer yet) ⇒ no CTA. "Use open slot" and "stake one
       more" are both claims about how many slots are spoken for, and we don't
       know that until the engine answers. The row keeps its plain caption. */
    if (!gateKnown || !slots || !slots.usageKnown) return {};
    return hasFreeSlot
      ? { onUseOpenSlot: onUseOpenSlot ? () => onUseOpenSlot(pair) : undefined }
      : { onStakeMore };
  };

  /* ── save (#9): allocation-only POST /config payload from the rows ──────── */
  const allocationsPayload = () => {
    const out = {};
    rows.forEach((r) => {
      const pct = Number(r.allocationPct) || 0;
      if (pct > 0) out[wireKey(r.pair)] = { pct, leverage: Number(r.leverage) || 1 };
    });
    return out;
  };
  /* Allocated-but-not-automated pairs would be rejected server-side (allocations
     must be a subset of the allowlist) — surface that BEFORE the request. */
  const unautomatedAllocated = rows
    .filter((r) => (Number(r.allocationPct) || 0) > 0 && !allowSet.has(wireKey(r.pair)))
    .map((r) => r.pair);

  /* ── sizing floors (SUPERBETA-6, contract §1b) ───────────────────────────
     The engine computes each pair's minimum viable allocation and we render it.
     No `sizingFloors` on /status ⇒ `floors.known` is false ⇒ no marks, no
     warnings, no invented thresholds anywhere below. */
  const floors = useMemo(() => readSizingFloors(status), [status]);
  const rowFloor = (r) => (floors.known ? floorFor(floors, r.pair) : null);
  const rowBelowFloor = (r) => isBelowFloor(r.allocationPct, rowFloor(r));
  const belowFloorPairs = rows.filter(rowBelowFloor).map((r) => r.pair);

  /* ── the sum ≤ 100 invariant, live ──────────────────────────────────────── */
  const overBy = overAllocatedBy(rows);
  const overAllocated = overBy > 0;

  const canSave =
    dirty &&
    allocationsSupported &&
    !!status?.riskConfig &&
    !savingAllocations &&
    !overAllocated &&
    unautomatedAllocated.length === 0;

  /* Post, then check the ENGINE'S echo — see allocationModel.compareAllocationEcho.
     `dirty` clears on any completed save so the rows go back to rendering the
     engine's answer; an unconfirmed save is reported as unconfirmed rather than
     papered over with the local numbers. */
  const commitSave = async (payload) => {
    setPendingBelowFloor(null);
    const result = await onSaveAllocations(payload);
    if (result && result.ok) {
      setDirty(false);
      setSaveResult({ confirmed: !!result.confirmed });
      return;
    }
    /* ARC-54 — a failed save must not leave the previous save's confirmation on
       screen. Without this, a member whose second save 400s keeps reading
       "Allocations saved" from the first one while the engine stored nothing. */
    setSaveResult(null);
  };

  const handleSave = async () => {
    if (!onSaveAllocations) return;
    const payload = allocationsPayload();
    /* Warn and require acknowledgment — never block. The floor moves with the
       member's balance, so an allocation that is valid today can fall below it
       after a drawdown; blocking would fight them over a moving target
       (contract §1b rule 4). */
    if (belowFloorPairs.length > 0) {
      setPendingBelowFloor({ payload, pairs: belowFloorPairs });
      return;
    }
    await commitSave(payload);
  };

  /* ── per-row honesty warnings (#9, extended by SUPERBETA-6) ─────────────
     Over-cap: the engine clamps every trade to maxCollateralPctPerTrade, so an
     allocation above it will size at the cap, not the allocation.
     Below-floor: the engine's own `sizingFloors` minimum for this pair — the
     RATIFIED source. Where it's absent we fall back to the older pairMeta
     computation (also engine data, but with the arithmetic done here, which is
     why the floors contract exists to replace it). Neither ⇒ no warning. */
  const maxPct = Number(status?.riskConfig?.maxCollateralPctPerTrade);
  const pairMeta = status?.pairMeta || {};
  const rowWarning = (r) => {
    const pct = Number(r.allocationPct) || 0;
    if (!pct || !status?.riskConfig) return null;
    if (Number.isFinite(maxPct) && pct > maxPct) {
      return `Above your ${maxPct}% per-trade risk cap — the engine will size this pair at ${maxPct}%, not ${pct}%.`;
    }
    const floor = rowFloor(r);
    if (floor) {
      return isBelowFloor(pct, floor)
        ? `${belowFloorSummary(floor)} ${belowFloorDetail(pct, floor, floors.balanceUsd)}`
        : null;
    }
    const meta = pairMeta[wireKey(r.pair)];
    const coll = Number(status?.collateralUsdc);
    if (meta && Number.isFinite(coll) && coll > 0) {
      const lev = Number(r.leverage) || 1;
      const notional = ((coll * pct) / 100) * lev;
      if (notional < meta.minPositionSizeUsd) {
        return `Too small to trade: ${pct}% of ${fmtUsdc(coll)} at ${lev}x is under gTrade's ${fmtUsd(
          meta.minPositionSizeUsd
        )} minimum — the engine will skip this pair's signals.`;
      }
    }
    return null;
  };

  /* Keyed on the WIRE key, not the raw string: /status returns whatever form the
     engine holds ("BTCUSD" or "BTC/USD") and the rows are display pairs, so a
     raw-string map silently misses every position on one of the two. */
  const positionsByPair = useMemo(() => {
    const map = {};
    (status?.openPositions || []).forEach((p) => {
      if (p?.pair) map[wireKey(p.pair)] = p;
    });
    return map;
  }, [status]);

  /* Per-pair PnL (ARC-34). /status has no PnL field at all; GET /report does,
     keyed by pairKey, and flags an unpriced position with a null. Absent or
     unpriced ⇒ the row keeps saying "PnL —" rather than showing a zero, which
     on a money row reads as "flat" and is a different claim entirely. */
  const pnlByPair = useMemo(() => {
    const map = {};
    (Array.isArray(reportPositions) ? reportPositions : []).forEach((p) => {
      const key = wireKey(p?.pairKey);
      if (!key) return;
      const pnl = Number(p?.unrealizedPnlUsd);
      if (Number.isFinite(pnl)) map[key] = pnl;
    });
    return map;
  }, [reportPositions]);

  const delegated = !!status?.delegationOk;
  const collateral = status?.collateralUsdc;

  /* ── ARC-46: the connected wallet and the ENROLLED wallet can now differ ──
     This console shows the address the browser wallet has selected, and every
     figure in it comes from /status — which is scoped to the member's ACCOUNT
     and reports their enrolled trading wallet. Those were the same address for
     everyone until "Add wallet" shipped; picking a second wallet in the
     extension now switches the address in this header without switching whose
     balance is underneath it.

     A money figure sitting under the wrong address is the worst kind of quiet
     wrong, so where the engine names a different wallet than the one connected,
     the console says which one the numbers belong to. Nothing is hidden and no
     figure is changed — the member is told what they are looking at. */
  const enrolledWallet = status?.wallet;
  const viewingOtherWallet =
    Boolean(enrolledWallet) &&
    Boolean(account) &&
    String(enrolledWallet).toLowerCase() !== String(account).toLowerCase();

  /* What the member has typed, and what is left as cash. Over 100% there IS no
     cash remainder — the bar says over-allocated instead of pretending to a
     0% cash line that would read as a valid, fully-invested plan. */
  const investedRaw = investedTotal(rows);
  const investedPct = clamp(investedRaw, 0, 100);
  const cashPct = overAllocated ? 0 : 100 - investedPct;
  /* How many of these strategies the member has actually turned on — the
     allowlist, whole. ARC-45: this used to be filtered by the unlocked window
     too, which undercounted a member whose automated pairs sat past it; an
     automated pair is automated whatever the stake is doing behind it. */
  const automatedCount = rows.filter((r) => allowSet.has(wireKey(r.pair))).length;

  return (
    <section className="sa-console" aria-label="Connected wallet">
      {/* ── console header ─────────────────────────────────────────────── */}
      <div className="sa-chead">
        <div className="sa-cname">
          <input
            type="text"
            value={name}
            onChange={(e) => renameWallet(e.target.value)}
            className="w-[200px] px-2.5 py-1.5 text-[15px] font-medium"
            aria-label="Wallet name"
            title="Wallet name is saved on this device only."
          />
          <span className="sa-addr tnum">{shortAddr(account)}</span>
          {/* The delegate light: cyan and pulsing when live, unlit when not. */}
          <span className={["sa-live", delegated ? "" : "sa-live-off"].join(" ").trim()}>
            <span className="sa-dot" aria-hidden="true" />
            {delegated ? "Delegate active" : "No delegate"}
          </span>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="sa-cval">
            <p className="sa-cval-v">{fmtUsdc(collateral)}</p>
            <p className="sa-cval-n">
              {automatedCount} of {rows.length} automated ·{" "}
              {overAllocated ? (
                <span className="text-arc-warn">{investedRaw}% allocated — over 100%</span>
              ) : (
                <>
                  {investedPct}% invested · {cashPct}% cash
                </>
              )}
            </p>
          </div>
          {/* Always-visible Revoke — the safety switch. */}
          <div className="text-right">
            <Button
              variant="secondary"
              onClick={onRevoke}
              disabled={revoking}
              className="!text-arc-neg !ring-arc-neg/40"
            >
              {revoking ? "Revoking…" : "Revoke"}
            </Button>
            <p className="mt-1.5 text-[10.5px] text-arc-ink-4 max-w-[190px]">
              Stops automated trading instantly. Your funds stay yours.
            </p>
          </div>
        </div>
      </div>

      {/* Where these rows' numbers come from — the sentence changes with the
          connection, because the same wording cannot be true of both.
          Engine connected ⇒ the values ARE the member's, and the only local
          thing left is the catalog of pairs on offer. Not connected ⇒ every
          figure below is modelled, and the demo label says so. */}
      <div className="sa-chead" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <p className="text-caption text-arc-ink-3 m-0">
          {engineLive ? (
            <>
              <span className="sa-st sa-st-auto mr-2" data-testid="sa-live-label">
                {LIVE_DATA_LABEL}
              </span>
              Delegate status, collateral, allocations and open positions are read
              from the engine. The catalog of pairs on offer is a local list.
              Allocations save to your account but don&apos;t trade yet.
            </>
          ) : (
            <>
              <span className="sa-st sa-st-idle mr-2" data-testid="sa-demo-label">
                {DEMO_DATA_LABEL}
              </span>
              Every figure in this console comes from a local model, not your
              account. Allocations save to your account but don&apos;t trade yet.
            </>
          )}
        </p>
        {viewingOtherWallet && (
          <p
            className="text-caption text-arc-warn mt-2 mb-0"
            role="status"
            data-testid="sa-wallet-mismatch-note"
          >
            Your wallet is currently on {shortAddr(account)}, but the figures above
            are your enrolled trading wallet, {shortAddr(enrolledWallet)}. Switch
            back in your wallet to act on this account.
          </p>
        )}
      </div>

      {/* ── live sum bar (SUPERBETA-6) ────────────────────────────────────
          Invested vs cash as one track, so the 100% budget is visible while
          editing rather than discovered at save. Over 100% the bar turns amber
          and states the overshoot; the save is blocked until it's back inside
          the budget, because the engine would reject the whole patch anyway. */}
      <div className="sa-sumbar" data-testid="sa-sum-bar">
        <div className="sa-sumtrack" aria-hidden="true">
          <div
            className={["sa-sumfill", overAllocated ? "sa-sumfill-over" : ""].join(" ").trim()}
            style={{ width: `${Math.min(100, investedRaw)}%` }}
          />
        </div>
        <p className="sa-sumtxt" role="status">
          {overAllocated ? (
            <span className="sa-warn">
              <TriangleAlert size={12} className="inline-block mr-1 -mt-0.5" aria-hidden="true" />
              Over-allocated: {investedRaw}% across your strategies, {overBy}% more than you
              have. Bring the total to 100% or less to save — allocations above 100% are
              rejected in full, not trimmed.
            </span>
          ) : (
            <>
              <b>{investedPct}%</b> invested across your strategies · <b>{cashPct}%</b> stays
              as cash collateral.
            </>
          )}
        </p>
        {/* Floors unknown is a state, and saying so beats a silent absence —
            but it is stated as an absence, never filled in with a guess. */}
        {!floors.known && !!status?.riskConfig && (
          <p className="sa-floor-note" data-testid="sa-floors-unknown">
            Per-pair minimums aren&apos;t available from the engine yet, so no minimum is
            marked on these sliders.
          </p>
        )}
      </div>

      {/* ── slot counter (ARC-45) ────────────────────────────────────────────
          The engine's own `slotsUsed` and `slotsTotal`, side by side, at the
          point the member is deciding what to turn on. It renders only where
          BOTH are readable — half a fraction is worse than none, and a count we
          could not read must never appear as a digit (ARC-53). */}
      {gateEngaged &&
        typeof slotGate?.slotsUsed === "number" &&
        typeof slotGate?.slotsTotal === "number" && (
        <p className="sa-slotcount" data-testid="sa-slot-counter">
          <b className="tnum">
            {slotGate.slotsUsed} of {slotGate.slotsTotal}
          </b>{" "}
          slots in use
          {availableKnown ? (
            <span className="sa-slotcount-n">
              {availableCount === 0
                ? " · none free"
                : ` · ${availableCount} free`}
            </span>
          ) : null}
        </p>
      )}

      {/* strategy rows — the member may turn on the next `slotsAvailable` of
          the pairs they have not already automated, and no more; all of them
          Locked when the slot pool could not be read (ARC-53/ARC-45).
          TODO(backend, supervised): signal-time enforcement is ARC-43. */}
      <div>
        {rows.map((r) => (
          <PairRow
            key={r.pair}
            pair={r.pair}
            locked={rowLocked(r)}
            automated={allowSet.has(wireKey(r.pair))}
            allocationPct={r.allocationPct}
            leverage={r.leverage}
            onAllocationChange={(v) => updateAllocation(r.pair, v)}
            onLeverageChange={(v) => updateRow(r.pair, { leverage: v })}
            position={positionsByPair[wireKey(r.pair)]}
            pnlUsd={pnlByPair[wireKey(r.pair)]}
            warning={rowWarning(r)}
            floor={rowFloor(r)}
            belowFloor={rowBelowFloor(r)}
            venue={r.venue}
            paused={pausedKeys.has(wireKey(r.pair))}
            /* To reach the Jth un-allocated row the member needs J+1 free
               slots and has `availableCount` — a claim we may only make with a
               slot pool in hand (ARC-53). */
            stakeMoreNeeded={Math.max(
              1,
              (unallocatedRank.get(wireKey(r.pair)) ?? 0) + 1 - availableCount
            )}
            /* False ⇒ the row says the read failed and shows NO count. An
               unreadable `slotsAvailable` is as unknown as an unreadable
               total, so both of them suppress the digit (ARC-45). */
            stakeKnown={!gateEngaged || (gateKnown && availableKnown)}
            /* SUPERBETA-8 — unlocked rows only. A locked row has no strategy to
               drive yet, so offering to connect one would be a dead end. */
            tvConnected={Boolean(connectedPairKeys?.has(wireKey(r.pair)))}
            onConnectTradingView={
              onConnectTradingView && !rowLocked(r)
                ? () => onConnectTradingView(r.pair)
                : undefined
            }
            {...(rowLocked(r) ? lockedCta(r.pair) : {})}
          />
        ))}

        {/* cash row — needs no stake */}
        <div className="sa-row">
          <div className="sa-pair">
            <span className="sa-nm inline-flex items-center gap-2">
              <Banknote size={15} className="text-arc-ink-3" aria-hidden="true" /> Cash
            </span>
            <span className="sa-st sa-st-idle">Idle</span>
          </div>
          <p className="sa-lev">No stake needed · held as USDC collateral</p>
          <p className={["sa-money", collateral == null ? "sa-money-none" : ""].join(" ").trim()}>
            {collateral != null ? fmtUsdc(collateral) : "—"}
          </p>
          <div className="sa-alloc">
            <div className="sa-bar" aria-hidden="true">
              <div className="sa-fill sa-fill-cash" style={{ width: `${cashPct}%` }} />
            </div>
            <span className="sa-pct">{cashPct}%</span>
          </div>
        </div>
      </div>

      {/* ── empty state (SUPERBETA-8) ───────────────────────────────────────
          No strategy has ever sent the engine a signal, so the rows above are
          allocation without a source. One CTA, aimed at the first row they can
          actually use. `connectedPairKeys` null ⇒ the engine hasn't answered
          and we don't know — so nothing renders, rather than an empty state
          asserted from missing data. */}
      {onConnectTradingView && connectedPairKeys?.size === 0 && firstUnlockedPair && (
        <div
          className="px-6 py-5 border-t border-arc-line flex items-center gap-3 flex-wrap"
          data-testid="sa-tv-empty-state"
        >
          <div className="flex-1 min-w-[240px]">
            <p className="text-body text-arc-ink">No TradingView alerts connected yet</p>
            <p className="text-caption text-arc-ink-3 mt-0.5">
              Your strategies run on alerts you send from TradingView. It takes
              about a minute.
            </p>
          </div>
          <Button onClick={() => onConnectTradingView(firstUnlockedPair)}>
            Connect TradingView
          </Button>
        </div>
      )}

      {/* save row (#9) — persistence only; execution stays gated until the
          engine's audit-gated sizing flag is armed */}
      <div className="px-6 py-5 flex items-center gap-3 flex-wrap border-t border-arc-line">
        <Button onClick={handleSave} disabled={!canSave}>
          {savingAllocations ? "Saving…" : "Save allocations"}
        </Button>
        {unautomatedAllocated.length > 0 && (
          <span role="status" className="text-caption text-arc-warn">
            {unautomatedAllocated.join(", ")}{" "}
            {unautomatedAllocated.length === 1 ? "isn't" : "aren't"} automated —
            enable {unautomatedAllocated.length === 1 ? "it" : "them"} in
            &ldquo;Your risk limits&rdquo; below before allocating.
          </span>
        )}
        {dirty && !allocationsSupported && (
          <span className="text-caption text-arc-ink-3">
            Saving needs the signalsApi allocations patch
            (patches/signalsApi-allocations.patch) applied.
          </span>
        )}
        {dirty && allocationsSupported && !status?.riskConfig && (
          <span className="text-caption text-arc-ink-3">
            Connect the backend and enroll to save allocations.
          </span>
        )}
        {overAllocated && (
          <span role="status" className="text-caption text-arc-warn">
            Saving is blocked while your strategies total more than 100%.
          </span>
        )}
      </div>

      {/* ARC-54 — the engine's 400 for this save, on the surface that sent it.
          The tab-level banner still carries the same message; this is the copy
          the member sees without leaving the allocation controls. */}
      {rejection && (
        <div className="px-6 pb-5">
          <RejectionNotice rejection={rejection} testId="sa-alloc-rejection" />
        </div>
      )}

      {/* ── below-minimum acknowledgment (SUPERBETA-6) ─────────────────────
          The warning is restated in full at the moment of saving, with the
          per-pair numbers, and the member confirms it themselves. The save is
          warned, never blocked: the floor moves with their balance, so what is
          below the minimum today can be above it tomorrow. */}
      {pendingBelowFloor && (
        <Notice
          tone="warn"
          className="mx-6 mb-5"
          role="alertdialog"
          aria-label="Confirm saving an allocation below its minimum"
          data-testid="sa-below-floor-confirm"
        >
          <p className="text-sm text-arc-warn font-semibold m-0">
            {pendingBelowFloor.pairs.join(", ")}{" "}
            {pendingBelowFloor.pairs.length === 1 ? "is" : "are"} below the minimum
            allocation the engine reports for{" "}
            {pendingBelowFloor.pairs.length === 1 ? "it" : "them"}.
          </p>
          <ul className="mt-2 mb-3 space-y-1 text-caption text-arc-ink-2 list-none p-0">
            {rows.filter(rowBelowFloor).map((r) => (
              <li key={r.pair}>
                <b>{r.pair}</b> —{" "}
                {belowFloorDetail(r.allocationPct, rowFloor(r), floors.balanceUsd)}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={() => commitSave(pendingBelowFloor.payload)}
              disabled={savingAllocations}
            >
              {savingAllocations ? "Saving…" : "Save anyway"}
            </Button>
            <Button variant="secondary" onClick={() => setPendingBelowFloor(null)}>
              Go back and adjust
            </Button>
          </div>
        </Notice>
      )}

      {/* ── what the ENGINE said, not what we sent ─────────────────────────
          The values on screen come from the refetched /status. A save whose
          echo didn't come back is reported as unconfirmed — never as success.
          This is the silent-drop failure the contract documents: /config used
          to accept allocations, return 200 and discard them, while the UI
          echoed local state and reported them saved. */}
      {saveResult && (
        <p
          role="status"
          data-testid="sa-save-echo"
          className={[
            "px-6 pb-4 text-caption max-w-[92ch]",
            saveResult.confirmed ? "text-arc-ink-3" : "text-arc-warn",
          ].join(" ")}
        >
          {saveResult.confirmed ? (
            SAVED_STATUS_LINE
          ) : (
            <>
              <TriangleAlert size={12} className="inline-block mr-1 -mt-0.5" aria-hidden="true" />
              Not confirmed by engine — your allocations were sent, but the engine did not
              return them. The percentages above are what the engine currently holds, not
              what you just entered. Try saving again; if it keeps happening, nothing has
              been stored.
            </>
          )}
        </p>
      )}

      {/* execution gate notice — keep blunt */}
      <p
        className="px-6 pb-5 text-caption text-arc-ink-3 max-w-[92ch]"
        data-testid="sa-unarmed-line"
      >
        {UNARMED_STATUS_LINE} Allocation and leverage settings save to your
        account and will drive the upcoming allocation engine — but they do not
        trade yet. Per-pair allocation sizing stays off until its code audit
        clears it. Today the engine trades only per your saved risk limits and
        pair allowlist (see &ldquo;Your risk limits&rdquo; below), with leverage
        hard-capped at 2x.
      </p>
    </section>
  );
};

export default WalletSection;
